import { Observable, Subject, timer } from 'rxjs';
import { AFirmaAlgorithmType, AFirmaFormatType, FileDetailsDTO, I18nData, SignatureFormatDTO } from '../../../app/api/plyca-dtos';

import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Configuration } from '../../../app/conf/app.constants';
import { TokenService } from '../../../app/token.service';
import { catchError, switchMap, take, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { UserPrefsService } from '../../../app/store/user-prefs.service';
import { SignaturePrefs, UserPrefs } from '../../../app/api/UserPrefs-dto';
import { PlycaDataService } from '../../plyca.service';
import { BaseSignatureProvider } from './signature.provider';
import {
  SignatureRequest, SignatureResponse,
  Certificate, SignatureStatus, CertificateSelectionResponse,
  SignatureProviderType
} from '../signature.objects';
import { SignatureUtils } from '../../signature.utils';
import { UIService } from '../../../app/ui.service';
import * as lodash from 'lodash';

declare var AutoScript: any;

const KEYSTORES = [
  { label: 'Default', value: '', devices: '' },
  { label: 'Windows', value: AutoScript.KEYSTORE_WINDOWS, devices: 'windows' },
  { label: 'Apple', value: AutoScript.KEYSTORE_APPLE, devices: 'mac' },
  { label: 'Mozilla', value: AutoScript.KEYSTORE_MOZILLA, devices: 'windows,mac,linux' }
];

const MAX_MILIS = 60000;

@Injectable({
  providedIn: 'root'
})
export class AfirmaSignatureProvider extends BaseSignatureProvider {

  private appletCargado = false;
  private keystoreLoad: any;
  private userPrefs: any;

  constructor(plycaService: PlycaDataService,
    translateService: TranslateService,
    private _configuration: Configuration,
    private tokenService: TokenService,
    private userPrefsService: UserPrefsService,
    private uiService: UIService,
    signatureUtils: SignatureUtils) {

    super(plycaService, translateService, signatureUtils);
    this.subscribe(this.userPrefsService.getUserPrefs(),
      (prefs: UserPrefs) => {
        this.userPrefs = prefs || new UserPrefs();

        if (!this.userPrefs.signaturePrefs) {
          this.userPrefs.signaturePrefs = new SignaturePrefs();
        }
        if (!this.userPrefs.signaturePrefs.keystore) {
          this.userPrefs.signaturePrefs.keystore = KEYSTORES[0].value;
        }
      });
  }

  public sign(request: SignatureRequest): Observable<SignatureResponse> {
    // definir formato firma y si es firma o contrafirma
    let signatureFormat: SignatureFormatDTO;
    let signatureFunction: Function;
    const file = request.file;
    const documentClass = request.documentClass;
    if (
      file.signatures &&
      file.signatures.length > 0
    ) {
      signatureFormat = this.getSignatureFormat(
        file.signatures[0].signatureFormat
      );
      if (file.signatures[0].signatureFormat.aFirmaFormat ===
        AFirmaFormatType.PAdES || file.signatures[0].signatureFormat.oneSignaturePerFile) {
        console.log('emitiendo firma separada (nuevo fichero)');
        signatureFunction = this.signData;
      } else {
        console.log('emitiendo contrafirma');
        signatureFunction = this.counterSignData;
      }
    } else {
      console.log('emitiendo firma', documentClass);
      signatureFormat = this.chooseSignatureFormat(
        file,
        documentClass.configuration.preferredSignatureFormats
      );
      signatureFunction = this.signData;
    }
    if (signatureFormat.aFirmaParams) {
      if (!lodash.isMap(signatureFormat.aFirmaParams)) {
        // viene en formato objeto, transformarlo a mapa
        signatureFormat.aFirmaParams = new Map<string, string>(Object.entries(signatureFormat.aFirmaParams));
      }
    } else {
      request.signatureFormat.aFirmaParams = new Map();
    }
    request.signatureFormat = signatureFormat;
    // obtener contenido a firmar y firmar
    if (request.certificate) {
      if (!request.signatureFormat.aFirmaParams) {
        request.signatureFormat.aFirmaParams = new Map();
      }
      request.signatureFormat.aFirmaParams.set('filters', 'encodedcert:' + request.certificate.certificateB64);
    }
    return this.getContentToSign(request.file).pipe(
      switchMap((b64Content: string) => {
        const signSuccessCallback = this.getSuccessCallback(request, request.subscription);
        const signErrorCallback = this.getErrorCallback(request, request.subscription);
        if (AutoScript.isAppOpened()) {
          // si está conectado lanzar firma
          this.internalSignWithThreadControl(signatureFunction, signSuccessCallback, signErrorCallback, request.subscription,
            signatureFormat.aFirmaAlgorithm, signatureFormat.aFirmaFormat, b64Content,
            request.comment, request.signatureFormat.aFirmaParams);
        } else {
          // si no está conectado lanzar firma si se chequea correctamente instalacion que levanta autofirma
          AutoScript.checkStatus(
            function () {
              this.internalSignWithThreadControl(signatureFunction, signSuccessCallback, signErrorCallback, request.subscription,
                signatureFormat.aFirmaAlgorithm, signatureFormat.aFirmaFormat, b64Content,
                request.comment, request.signatureFormat.aFirmaParams);
            }.bind(this),
            function () {
              this.uiService.showMessage(new I18nData('Signature.Autofirma.Error.Connection'));
            }.bind(this)
          )
        }
        // realizar firma
        return request.subscription;
      })
    );
  }

  public selectCertificate(): Observable<CertificateSelectionResponse> {
    this.loadMiniApplet();
    this.setStorageServlets();
    const params = new Map<string, string>();
    params.set('headless', 'false');
    this.addSigningAndNonExpiredCertsFilter(params);
    const responseObs = new Subject<CertificateSelectionResponse>();
    // hilo para controlar cierre por de autofirma por parte del S.O. o la propia aplicacion de autofirma
    const errorCallback = this.getCertificateErrorCallback(responseObs);
    this.launchThreadControl(responseObs, errorCallback);
    AutoScript.selectCertificate(this.formatParameteMap(params), this.getCertificateSuccessCallback(responseObs), errorCallback);
    return responseObs;
  }

  public isOpeningAfirma(): boolean {
    return AutoScript.isOpeningAfirma();
  }

  public setOpeningAfirma(opening: boolean): void {
    AutoScript.setOpeningAfirma(opening);
  }

  private getCertificateSuccessCallback(responseObs: Subject<CertificateSelectionResponse>) {
    return function (certificateB64: string) {
      const response = new CertificateSelectionResponse();
      response.status = SignatureStatus.OK;
      const certificate = new Certificate();
      certificate.certificateB64 = certificateB64;
      response.certificate = certificate;
      responseObs.next(response);
      responseObs.complete();
    };
  }

  private getCertificateErrorCallback(responseObs: Subject<CertificateSelectionResponse>) {
    return function (errorType, errorMessage) {
      console.error('Error en la seleccion del certificado. Tipo error:', errorType, '. Mensaje de error:', errorMessage);
      const response = new CertificateSelectionResponse();
      response.status = ('es.gob.afirma.core.AOCancelledOperationException' === errorType) ? SignatureStatus.CANCELLED
        : SignatureStatus.ERROR;
      response.errorMessage = errorMessage;
      responseObs.next(response);
      responseObs.complete();
    };
  }

  public check(): Observable<SignatureStatus> {
    this.loadMiniApplet();
    return new Observable(observer => {
      AutoScript.checkStatus(
        function (args: any) {
          console.log('Autofirma OK', args);
          observer.next(SignatureStatus.OK); // Emitir el valor a través del observable
          observer.complete();  // Indicar que la operación ha finalizado
        },
        function (args: any) {
          console.error('Error al conectar con Autofirma', args);
          observer.error(SignatureStatus.ERROR); // Emitir el error a través del observable
        });
    });
  }

  public getKeystores() {
    return KEYSTORES.filter(keystore =>
      keystore.devices === '' || keystore.devices.indexOf(this.getOSName()) >= 0
    );
  }

  public setStickySignatory(stick: boolean): void {
    AutoScript.setStickySignatory(stick);
  }

  /**
 * Identifica el sistema operativo del usuario para notificarselo al servicio
 * de generacion del fichero de despliegue JNLP.
 * @returns Codigo del sistema operativo o "unknown" si no se conoce.
 */
  private getOSName(): string {
    let osName = 'unknown';
    if (navigator.appVersion.indexOf('Win') !== -1) {
      osName = 'windows';
    }
    if (navigator.appVersion.indexOf('Mac') !== -1) {
      osName = 'mac';
    }
    if (navigator.appVersion.indexOf('Linux') !== -1) {
      osName = 'linux';
    }
    return osName;
  }

  private getSupportedClientFormat(): SignatureFormatDTO[] {
    let supportedClientFormat: SignatureFormatDTO[] = [];
    if (AutoScript.isIOS()) {
      console.log('Client is IOS');
      let padesType: SignatureFormatDTO = {} as SignatureFormatDTO;
      padesType.aFirmaFormat = AFirmaFormatType.PAdESTri;
      supportedClientFormat.push(padesType);
      let cadesType: SignatureFormatDTO = {} as SignatureFormatDTO;
      cadesType.aFirmaFormat = AFirmaFormatType.CAdES;
      supportedClientFormat.push(cadesType);
      let xadesType: SignatureFormatDTO = {} as SignatureFormatDTO;
      xadesType.aFirmaFormat = AFirmaFormatType.XAdESTri;
      supportedClientFormat.push(xadesType);
    } else if (AutoScript.isAndroid()) {
      console.log('Client is Android');
      let padesType: SignatureFormatDTO = {} as SignatureFormatDTO;
      padesType.aFirmaFormat = AFirmaFormatType.PAdES;
      supportedClientFormat.push(padesType);
      let cadesType: SignatureFormatDTO = {} as SignatureFormatDTO;
      cadesType.aFirmaFormat = AFirmaFormatType.CAdES;
      supportedClientFormat.push(cadesType);
      let xadesType: SignatureFormatDTO = {} as SignatureFormatDTO;
      xadesType.aFirmaFormat = AFirmaFormatType.XAdESTri;
      supportedClientFormat.push(xadesType);
    } else {
      let padesType: SignatureFormatDTO = {} as SignatureFormatDTO;
      padesType.aFirmaFormat = AFirmaFormatType.PAdES;
      supportedClientFormat.push(padesType);
      let xadesType: SignatureFormatDTO = {} as SignatureFormatDTO;
      xadesType.aFirmaFormat = AFirmaFormatType.XAdES;
      supportedClientFormat.push(xadesType);
      let cadesType: SignatureFormatDTO = {} as SignatureFormatDTO;
      cadesType.aFirmaFormat = AFirmaFormatType.CAdES;
      supportedClientFormat.push(cadesType);
      let pk7Type: SignatureFormatDTO = {} as SignatureFormatDTO;
      pk7Type.aFirmaFormat = AFirmaFormatType.CMS_PKCS7;
      supportedClientFormat.push(pk7Type);
    }
    console.log('Suported client formats:', supportedClientFormat);
    return supportedClientFormat;
  }

  private counterSignData(
    base64: string,
    algorithm: AFirmaAlgorithmType,
    format: AFirmaFormatType,
    comment: string,
    params: Map<string, string>,
    successCallback: Function,
    errorCallback: Function
  ): string {
    let signParameters: Map<string, string> = this.initSignatureParams(params, format, comment);
    signParameters.set('target', 'leafs');
    return this.internalSignData(
      AutoScript.counterSign,
      base64,
      algorithm,
      'AUTO',
      signParameters,
      successCallback,
      errorCallback
    );
  }

  private signData(
    base64: string,
    algorithm: AFirmaAlgorithmType,
    format: AFirmaFormatType,
    comment: string,
    params: Map<string, string>,
    successCallback: Function,
    errorCallback: Function
  ): string {
    let signParameters: Map<string, string> = this.initSignatureParams(params, format, comment);

    if (format === AFirmaFormatType.XAdES || format === AFirmaFormatType.XAdESTri) {
      // Las firmas XAdES son INTERNALLY DETACHED (se crea un nodo CONTENT)
      signParameters.set('format', 'XAdES Detached');
      // A autofirma se le pasa el documento a firmar en base64 pero internamente lo decodifica y maneja el binario
      // Por lo que si queremos que el nodo CONTENT se guarde a su vez en base64 es necesario un segundo encoding
      base64 = AutoScript.getBase64FromText(base64);
      // Y queremos que en la firma se indique que es necesaria la transaformación a Base64 para obtener el documento firmado
      signParameters.set('encoding', 'base64');
      signParameters.set('xmlTransforms', '1');
      signParameters.set('xmlTransform0Type', 'http://www.w3.org/2000/09/xmldsig#base64');
    }


    return this.internalSignData(
      AutoScript.sign,
      base64,
      algorithm,
      format,
      signParameters,
      successCallback,
      errorCallback
    );
  }

  private internalSignData(
    miniAppletFunction: Function,
    base64: string,
    algorithm: AFirmaAlgorithmType,
    format: AFirmaFormatType,
    params: Map<string, string>,
    successCallback: Function,
    errorCallback: Function
  ): string {
    this.loadMiniApplet();
    this.setStorageServlets();
    if (true) {
      console.log('firmando ...');
      console.log('algoritmo de firma:', algorithm);
      console.log('formato de firma:', format);
    }
    let signatureParams = params;
    if (!signatureParams || signatureParams.size === 0) {
      signatureParams = new Map<string, string>();
    }
    if (format === AFirmaFormatType.PAdESTri ||
      format === AFirmaFormatType.CAdESTri ||
      format === AFirmaFormatType.XAdESTri) {

      signatureParams.set('serverUrl', this._configuration.URL_BASE_AFIRMA + '/SignatureService');
    }

    let stringFormattedParams = this.formatParameteMap(signatureParams)
    if (true) {
      console.log('parametros de firma:', stringFormattedParams);
    }
    return miniAppletFunction.call(
      this,
      base64,
      algorithm,
      format,
      stringFormattedParams,
      successCallback,
      errorCallback
    );
  }
  private setStorageServlets(): void {
    let afirmaServlethParam: string =
      this._configuration.URL_BASE_AFIRMA +
      '/v1/storage/' +
      this.tokenService.getAccessToken();
    AutoScript.setServlets(afirmaServlethParam, afirmaServlethParam);
  }

  private formatParameteMap(params: Map<string, string>): string {
    let stringFormattedParams = '';
    let paramSeparator = '';
    params.forEach((value: string, key: string) => {
      stringFormattedParams += paramSeparator + key + '=' + value;
      paramSeparator = '\n';
    });
    return stringFormattedParams;
  }

  private loadMiniApplet() {
    if (!this.appletCargado || this.keystoreLoad !== this.userPrefs.signaturePrefs.keystore) {
      console.log('cargando applet...');
      if (this.userPrefs.signaturePrefs.keystore && this.userPrefs.signaturePrefs.keystore !== '') {
        AutoScript.cargarAppAfirma(this._configuration.URL_BASE_AFIRMA, this.userPrefs.signaturePrefs.keystore);
        this.keystoreLoad = this.userPrefs.signaturePrefs.keystore;
      } else {
        AutoScript.cargarAppAfirma(this._configuration.URL_BASE_AFIRMA);
        this.keystoreLoad = '';
      }
      if (this.translateService.currentLang === 'es' || this.translateService.currentLang === 'ca') {
        AutoScript.setLocale(this.translateService.currentLang);
      }
      // advertir al usuario en caso de desfase horario
      AutoScript.checkTime(
        AutoScript.CHECKTIME_RECOMMENDED,
        MAX_MILIS,
        this._configuration.URL_BASE_AFIRMA + '/v1/checkTime'
      );
      console.log('applet cargado');
      this.appletCargado = true;
    }
  }

  private initSignatureParams(params: Map<string, string>, format: AFirmaFormatType, comment: string): Map<string, string> {
    let signParameters: Map<string, string>;
    if (params) {
      signParameters = params;
    } else {
      signParameters = new Map<string, string>();
    }
    if (!signParameters.has('headless')) {
      signParameters.set('headless', 'true');
    }
    this.addSigningAndNonExpiredCertsFilter(signParameters);
    // Si es PAdES establecemos comentario
    if ((format === AFirmaFormatType.PAdES || format === AFirmaFormatType.PAdESTri) && comment) {
      signParameters.set('signReason', comment);
      // signParameters.set('padesSignReason', comment);
    }
    return signParameters;
  }

  private addSigningAndNonExpiredCertsFilter(signParameters: Map<string, string>): void {
    let filterValue = signParameters.get('filters');
    if (!filterValue) {
      filterValue = 'signingCert:;nonexpired:';
    } else {
      if (filterValue.indexOf('signingCert:') === -1) {
        filterValue += ';signingCertt:';
      }
      if (filterValue.indexOf('nonexpired:') === -1) {
        filterValue += ';nonexpired:';
      }
      filterValue += filterValue;
    }
    signParameters.set('filters', filterValue);
  }

  /**
 * Lanzar hilo de consulta para que en caso de pérdida de conexión se emita un error, se consuma
 * peticion de firma errónea y se continue con la siguiente firma si la hubiera
 *
 * @param signatureFunction funcion a ejecutar signData (firma) o counterSignData (contrafirma)
 * @param signSuccessCallback Callback de Autofirma cuando se firma correctamente
 * @param signErrorCallback Callback de Autofirma cuando hay error en la firma
 * @param signaturResponseSubjet obserbable donde se emite el resultado de firma
 * @param aFirmaAlgorithm Algoritmo de firma
 * @param aFirmaFormat Formato de firma
 * @param comment contenido a firmar en B64
 * @param comment comentario de firma
 * @param signatureParams parametros adicionales de firma

 */
  private internalSignWithThreadControl(signatureFunction: Function,
    signSuccessCallback: (b64Signature: string, certificateB64: string) => void,
    signErrorCallback: (errorType: any, errorMessage: any) => void,
    signaturResponseSubjet: Subject<SignatureResponse>,
    aFirmaAlgorithm: AFirmaAlgorithmType, aFirmaFormat: AFirmaFormatType, data: string, comment: string,
    signatureParams: Map<string, string>) {
    // b64: string, signatureFormat: SignatureFormatDTO, comment: string,
    // signatureRequest: SignatureRequest) {
    this.launchThreadControl(signaturResponseSubjet, signErrorCallback);
    // realizar firma
    signatureFunction.call(
      this,
      data,
      aFirmaAlgorithm,
      aFirmaFormat,
      comment,
      signatureParams,
      signSuccessCallback,
      signErrorCallback
    );
  }

  /**
   * hilo para controlar cierre por de autofirma por parte del S.O. o la propia aplicacion de autofirma
   * @param obs obserbable donde se emite el resultado de firma
   * @param errorCallback Callback de Autofirma cuando hay error en la firma
   */
  private launchThreadControl(obs: Observable<any>, errorCallback: (errorType: any, errorMessage: any) => void) {
    timer(3000, 5000).pipe( // emitir cada 5 segundos empezando desde ahora en 3 segundos
      takeUntil(obs), // mantener abierto hasta emisión de firma OK o ERR, o seleccion de certificado
      takeWhile(val => val < 61), // mantener abierta suscripcion hasta un maximo de 60 intentos (60 * 5 = 3 minutos)
      tap(() => {
        if (!AutoScript.isAppOpened()) {
          errorCallback('OtherType', this.translateService.instant('Signature.AutoFirma.Error.Timeout'));
        }
      })
    ).subscribe();
  }

  private getErrorCallback(signatureRequest: SignatureRequest, signaturResponseSubjet: Subject<SignatureResponse>) {
    return function (
      errorType: string,
      errorMessage: string
    ): void {
      console.error('Error en la seleccion del certificado. Tipo error:',
        errorType,
        '. Mensaje de error:'
        , errorMessage)
      let response = new SignatureResponse();
      response.request = signatureRequest;
      response.status = ('es.gob.afirma.core.AOCancelledOperationException' === errorType) ?
        SignatureStatus.CANCELLED : SignatureStatus.ERROR;
      response.errorMessage = this.getSignatureErrorMessage(errorType, errorMessage);
      // _this.isSigningProcActive = false;
      signaturResponseSubjet.next(response);
      signaturResponseSubjet.complete();
    }.bind(this);
  }

  private getSignatureErrorMessage(errorType: string, message: string): string {
    console.error(message);
    let msg: string;
    switch (errorType) {
      case 'es.gob.afirma.core.AOFormatFileException': {
        msg = 'Formato de firma no soportado';
        break;
      }
      case 'es.gob.afirma.keystores.AOCertificatesNotFoundException': {
        msg = 'No se han encontrado certificados con los que emitir la firma';
        break;
      }
      case 'es.gob.afirma.core.AOCancelledOperationException': {
        msg = 'Operación de firma cancelada por el usuario';
        break;
      }
      default: {
        msg = message;
      }
    }
    return msg;
  }

  private getSuccessCallback(signatureRequest: SignatureRequest, signaturResponseSubjet: Subject<SignatureResponse>) {
    return function (
      b64Signature: string,
      certificateB64: string
    ): void {
      this.subscribe(this.uploadAndProcessSignature(signatureRequest, b64Signature).pipe(
        catchError((error: any) => error),
        tap((response: SignatureResponse) => {
          signaturResponseSubjet.next(response);
          signaturResponseSubjet.complete();
        })
      ));
    }.bind(this);
  }

  private getSignatureFormat(
    supportedDocumentFormat: SignatureFormatDTO
  ): SignatureFormatDTO {
    let format: SignatureFormatDTO;
    if (supportedDocumentFormat) {
      let supportedClientFormat: SignatureFormatDTO[] = this.getSupportedClientFormat();
      let supportedFormats: SignatureFormatDTO[] = supportedClientFormat.filter(
        (clientFormat: SignatureFormatDTO) =>
          clientFormat.aFirmaFormat === supportedDocumentFormat.aFirmaFormat
      );
      if (supportedFormats) {
        format = supportedDocumentFormat;
      }
    }
    if (!format) {
      throw Error(
        'No es posible emitir una firma en un formato de firma: ' +
        supportedDocumentFormat.aFirmaFormat
      );
    }
    return format;
  }

  private chooseSignatureFormat(
    file: FileDetailsDTO,
    supportedDocumentFormat: SignatureFormatDTO[]
  ): SignatureFormatDTO {
    let format: SignatureFormatDTO;
    if (supportedDocumentFormat) {
      let supportedClientFormat: SignatureFormatDTO[] = this.getSupportedClientFormat();
      if (supportedClientFormat) {
        // se toma el primer formato admitido por el SO del cliente
        let fileExtension: string = this.getFileExtension(file);
        for (
          let index = 0;
          !format && index < supportedDocumentFormat.length;
          index++
        ) {
          let documentFormat: SignatureFormatDTO =
            supportedDocumentFormat[index];
          let supportedFormats: SignatureFormatDTO = supportedClientFormat.find(
            (clientFormat: SignatureFormatDTO) => {
              return (
                clientFormat.aFirmaFormat
                  .toString()
                  .startsWith(documentFormat.aFirmaFormat.toString()) &&
                (documentFormat.signingFileExtensions.indexOf('*') > -1 ||
                  documentFormat.signingFileExtensions.indexOf(fileExtension) >
                  -1)
              );
            }
          );
          if (supportedFormats) {
            format = documentFormat;
            // se sobreescribe el formato por si es trifase
            format.aFirmaFormat = supportedFormats.aFirmaFormat;
          }
        }
      }
    }
    if (!format) {
      throw Error(
        'No es posible emitir una firma en los formatos de firma admitidos: ' +
        supportedDocumentFormat
          .map(
            (documentFormat: SignatureFormatDTO) =>
              documentFormat.aFirmaFormat
          )
          .join(', ')
      );
    }
    console.log('Choosen signature format:', format);
    return format;
  }

  private getFileExtension(file: FileDetailsDTO): string {
    let extension: string;
    let fileName: string = file.name;
    let index: number = fileName.lastIndexOf('.');
    if (index > 0) {
      extension = fileName.substr(index + 1);
    }
    return extension;
  }

  protected getSignatureProviderType(): SignatureProviderType {
    return SignatureProviderType.AFIRMA;
  }

  isEnabled(): boolean {
    return !this.signatureUtils.isChromeOS();
  }

}
